import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { createMongoAbility } from '@casl/ability'
import { createContextualCan } from '@casl/react'
import { usePermissionContext } from '@/contexts/PermissionContext'
import { useAuthStore } from '@/store/authStore'
import { useProfileStore } from '@/store/profileStore'
import type { AppAbility, CaslRule } from './ability'
import {
  buildAbility,
  mergeRules,
  normalizeCaslSubject,
  rulesForGroups,
  stripDashboardRulesForRestrictedGroups,
} from './ability'

const DASHBOARD_PROFILE_TYPES = ['ENTITY', 'ORGANIZATION']

const BUSINESS_CLIENT_RULES: CaslRule[] = [
  { action: 'read', subject: 'Dashboard' },
  { action: 'manage', subject: 'Order' },
]

const AbilityContext = createContext<AppAbility>(createMongoAbility([]))

export const Can = createContextualCan(AbilityContext.Consumer)

interface AbilityProviderProps {
  children: ReactNode
}

/**
 * Extracts CASL rules from a profile object, handling both
 * GraphQL-transformed (casl_rules) and REST-format (casl_rules / caslRules) shapes.
 */
function extractRules(profile: Record<string, any> | undefined): CaslRule[] {
  const raw: any[] = profile?.casl_rules ?? profile?.caslRules ?? []
  if (!Array.isArray(raw)) return []
  return raw
    .filter((r) => r && typeof r.action === 'string' && typeof r.subject === 'string')
    .map((r) => ({
      action: r.action.toLowerCase(),
      subject: normalizeCaslSubject(r.subject),
    }))
}

function isDashboardType(profileType: string | undefined): boolean {
  if (!profileType) return false
  return DASHBOARD_PROFILE_TYPES.includes(profileType.toUpperCase())
}

function extractGroups(profile: Record<string, any> | null | undefined): string[] {
  if (!profile) return []
  const raw = profile.groups
  return Array.isArray(raw) ? raw.filter((g: any) => typeof g === 'string') : []
}

function isBusinessClient(profileType: string | undefined): boolean {
  return profileType?.toUpperCase() === 'BUSINESS_CLIENT'
}

/**
 * Determines whether the business client CASL rules should be used.
 * True when:
 *  - profileMode is explicitly 'business_client', OR
 *  - profileMode is 'entity' but no Entity/Organization profile exists
 *    and a BUSINESS_CLIENT profile does (auto-detect for users who only
 *    have a business client profile — prevents access-denied on first render
 *    before the ProfileSwitchContext effect can correct the mode).
 */
function shouldUseBusinessClientRules(
  profileMode: string,
  allProfiles: Record<string, any>[]
): boolean {
  if (profileMode === 'business_client') return true
  if (profileMode === 'entity') {
    const hasEntity = allProfiles.some((p) =>
      isDashboardType(p.profile_type ?? p.profileType)
    )
    if (!hasEntity) {
      return allProfiles.some((p) =>
        isBusinessClient(p.profile_type ?? p.profileType)
      )
    }
  }
  return false
}

export function AbilityProvider({ children }: AbilityProviderProps) {
  const { activeProfile, profiles, isSuperuser } = usePermissionContext()
  const storeUser = useAuthStore((s) => s.user)
  const profileMode = useProfileStore((s) => s.activeMode)

  const ability = useMemo(() => {
    const allProfiles: Record<string, any>[] = [
      ...profiles,
      ...(storeUser?.profiles ?? []),
    ] as any[]

    if (shouldUseBusinessClientRules(profileMode, allProfiles)) {
      return buildAbility(BUSINESS_CLIENT_RULES)
    }

    if (isSuperuser) {
      return buildAbility([{ action: 'manage', subject: 'all' }])
    }

    const dashboardEntityProfile = allProfiles.find((p) =>
      isDashboardType(p.profile_type ?? p.profileType)
    )

    // Prefer entity/org profile rules for this portal (active profile is often "provider" first)
    let rules: CaslRule[] = extractRules(dashboardEntityProfile)

    if (rules.length === 0) {
      rules = extractRules(activeProfile as any)
    }

    if (rules.length === 0) {
      for (const p of allProfiles) {
        const r = extractRules(p)
        if (r.length > 0) {
          rules = r
          break
        }
      }
    }

    const entityGroups = extractGroups(dashboardEntityProfile)
    const activeGroups = extractGroups(activeProfile as any)
    const userGroups = Array.isArray(storeUser?.groups)
      ? storeUser.groups!.filter((g): g is string => typeof g === 'string')
      : []

    const groupsForPolicy =
      entityGroups.length > 0 ? entityGroups : activeGroups.length > 0 ? activeGroups : userGroups

    // Defensive: ensure each recognized group's baseline rules are always present,
    // even when the backend sends non-empty but incomplete caslRules (e.g. MCP-only
    // rules that grant no Dashboard/Order/Finance/Provider access). Without this, such
    // rules skip the group fallback and lock the user out of every CASL-guarded route.
    // Applies to both dashboard profile types (ENTITY and ORGANIZATION) since
    // groupsForPolicy is derived from whichever dashboard profile is active.
    if (groupsForPolicy.length > 0) {
      rules = mergeRules(rules, rulesForGroups(groupsForPolicy))
    }

    // Strip last so it stays authoritative: dispatcher/accountant must never read the
    // Dashboard (admin is never stripped) even if backend or group rules grant it.
    rules = stripDashboardRulesForRestrictedGroups(rules, groupsForPolicy)

    return buildAbility(rules)
  }, [activeProfile, profiles, storeUser?.profiles, isSuperuser, profileMode])

  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  )
}

export function useAbility(): AppAbility {
  return useContext(AbilityContext)
}
